import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from '../hooks/use-toast';

const Footer = () => {
  const [email, setEmail] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    // 1. Validation
    if (!email) {
      toast({ 
        title: "Email required", 
        description: "Please enter a valid email.", 
        variant: "destructive" 
      });
      return;
    }

    try {
      // 2. Prepare the data for the PHP backend
      const formBody = new FormData();
      formBody.append("type", "newsletter"); // Tells the backend this is a signup
      formBody.append("email", email);

      // 3. Send the request
      const response = await fetch("https://aalign.io/send-mail.php", {
        method: "POST",
        body: formBody,
      });

      const result = await response.json();

      // 4. Handle response
      if (result.success) {
        toast({ 
          title: "Subscribed!", 
          description: "Welcome to AALIGN newsletter." 
        });
        setEmail(""); // Clear the input field
      } else {
        toast({ 
          title: "Error", 
          description: result.message, 
          variant: "destructive" 
        });
      }
    } catch (error) {
      // Handle network or syntax errors
      toast({ 
        title: "Network Error", 
        description: "Could not connect to the server. Please try again.", 
        variant: "destructive" 
      });
    }
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Newsletter Section */}
      <div className="border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl lg:text-4xl font-bold mb-4">
                Get the latest updates
              </h3>
              <p className="text-gray-400 text-lg">
                Join 1,000+ businesses receiving weekly insights on lead management and growth strategies.
              </p>
            </div>
            <form onSubmit={handleSubscribe} className="flex gap-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 h-14 px-6 rounded-xl"
              />
              <Button
                type="submit"
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-8 h-14 rounded-xl font-semibold shadow-lg"
              >
                Subscribe
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_aalign-replica/artifacts/kdftzdfh_Aalign_Logo_black.png" 
                alt="AALIGN Logo" 
                className="h-10 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              AI-powered lead management platform helping businesses achieve scalable B2B growth.
            </p>
            <div className="space-y-3">
              <a href="mailto:hello@aalign.io" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                <Mail className="w-5 h-5" />
                <span>hello@aalign.io</span>
              </a>
              <a href="tel:+13219997374" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                <Phone className="w-5 h-5" />
                <span>+1 321-999-7374</span>
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-lg font-bold mb-6">Product</h4>
            <ul className="space-y-3">
              <li><Link to="/features" className="text-gray-400 hover:text-white transition-colors">Features</Link></li>
              <li><Link to="/pricing" className="text-gray-400 hover:text-white transition-colors">Pricing</Link></li>
              <li><Link to="/integrations" className="text-gray-400 hover:text-white transition-colors">Integrations</Link></li>
              <li><Link to="/api-documentation" className="text-gray-400 hover:text-white transition-colors">API Documentation</Link></li>
              <li><Link to="/changelog" className="text-gray-400 hover:text-white transition-colors">Changelog</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-lg font-bold mb-6">Company</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/careers" className="text-gray-400 hover:text-white transition-colors">Careers</Link></li>
              <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/press-kit" className="text-gray-400 hover:text-white transition-colors">Press Kit</Link></li>
              <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Partners</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-lg font-bold mb-6">Resources</h4>
            <ul className="space-y-3">
              <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Case Studies</Link></li>
              {/* <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Status</Link></li> */}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400">
            © 2025 AALIGN. All rights reserved.
          </p>
          <div className="flex gap-8">
            <Link to="/refund-policy" className="text-gray-400 hover:text-white transition-colors text-sm">Refund Policy</Link>
            <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors text-sm">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="text-gray-400 hover:text-white transition-colors text-sm">Terms of Service</Link>
            <Link to="/cookie-policy" className="text-gray-400 hover:text-white transition-colors text-sm">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;